import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

// =============================================================================
// CARE GAP SUMMARY ENDPOINTS
// =============================================================================

/**
 * Get facility-level care gap summary
 * @param {string} facilityId - Facility ID
 * @param {string} dataSource - Data source ('production' or 'synthea')
 * @returns {Promise<Object>} Care gap summary data
 */
export async function getCareGapSummary(facilityId, dataSource = 'production') {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/careGaps/summary`,
      {
        params: { facilityId, dataSource },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting care gap summary:', error);
    throw error;
  }
}

/**
 * Get population summary by gap type
 * @param {string} facilityId - Facility ID
 * @param {string} dataSource - Data source ('production' or 'synthea')
 * @returns {Promise<Object>} Population summary by gap type
 */
export async function getCareGapPopulationSummary(facilityId, dataSource = 'production') {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/careGaps/population-summary`,
      {
        params: { facilityId, dataSource },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting population summary:', error);
    throw error;
  }
}

// =============================================================================
// PATIENT CARE GAP ENDPOINTS
// =============================================================================

/**
 * Get care gaps for a specific patient
 * @param {string} patientId - Patient ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Patient's care gaps
 */
export async function getPatientCareGaps(patientId, facilityId) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/careGaps/patient/${patientId}`,
      {
        params: { facilityId },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting patient care gaps:', error);
    throw error;
  }
}

// =============================================================================
// OUTREACH ENDPOINTS
// =============================================================================

/**
 * Get prioritized outreach list
 * @param {string} facilityId - Facility ID
 * @param {number} limit - Maximum number of patients to return
 * @param {string} dataSource - Data source ('production' or 'synthea')
 * @returns {Promise<Object>} Prioritized outreach list
 */
export async function getCareGapOutreachList(facilityId, limit = 100, dataSource = 'production') {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/careGaps/outreach-list`,
      {
        params: { facilityId, limit, dataSource },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting outreach list:', error);
    throw error;
  }
}

/**
 * Record an outreach attempt for a care gap
 * @param {string} gapId - Care gap ID
 * @param {Object} outreachData - Outreach data
 * @returns {Promise<Object>} Outreach record result
 */
export async function recordCareGapOutreach(gapId, outreachData) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/careGaps/${gapId}/outreach`,
      outreachData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error recording outreach:', error);
    throw error;
  }
}

// =============================================================================
// ANALYTICS ENDPOINTS
// =============================================================================

/**
 * Get care gap analytics and trends
 * @param {string} facilityId - Facility ID
 * @param {number} days - Number of days to analyze
 * @param {string} dataSource - Data source ('production' or 'synthea')
 * @returns {Promise<Object>} Analytics data
 */
export async function getCareGapAnalytics(facilityId, days = 30, dataSource = 'production') {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/careGaps/analytics`,
      {
        params: { facilityId, days, dataSource },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting care gap analytics:', error);
    throw error;
  }
}

// =============================================================================
// GAP CLOSURE ENDPOINTS
// =============================================================================

/**
 * Close a care gap (mark as addressed)
 * @param {string} gapId - Care gap ID
 * @param {Object} closureData - Closure data including facilityId, closureType, notes
 * @returns {Promise<Object>} Closure result
 */
export async function closeCareGap(gapId, closureData) {
  try {
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/careGaps/${gapId}/close`,
      closureData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error closing care gap:', error);
    throw error;
  }
}

// =============================================================================
// RISK TIER ENDPOINTS
// =============================================================================

/**
 * Get patients by care gap risk tier
 * @param {string} facilityId - Facility ID
 * @param {string} dataSource - Data source ('production' or 'synthea')
 * @returns {Promise<Object>} Risk tier data
 */
export async function getCareGapRiskTiers(facilityId, dataSource = 'production') {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/careGaps/risk-tiers`,
      {
        params: { facilityId, dataSource },
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting risk tiers:', error);
    throw error;
  }
}

// =============================================================================
// DATA HEALTH ENDPOINTS
// =============================================================================

/**
 * Get care gap data health status (admin only)
 * @returns {Promise<Object>} Data health status
 */
export async function getCareGapDataHealth() {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/careGaps/data-health`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting data health:', error);
    throw error;
  }
}

/**
 * Check care gaps service health
 * @returns {Promise<Object>} Service health status
 */
export async function getCareGapsServiceHealth() {
  try {
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/careGaps/health`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error checking care gaps service health:', error);
    throw error;
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get color for care gap category
 * @param {string} category - Gap category
 * @returns {string} Color code
 */
export function getCareGapCategoryColor(category) {
  const colors = {
    'screening': '#2196F3',      // Blue
    'immunization': '#4CAF50',   // Green
    'chronic_disease': '#FF9800', // Orange
    'preventive_visit': '#9C27B0', // Purple
  };
  return colors[category] || '#757575'; // Grey for unknown
}

/**
 * Get icon for care gap category
 * @param {string} category - Gap category
 * @returns {string} Icon name (Material Icons)
 */
export function getCareGapCategoryIcon(category) {
  const icons = {
    'screening': 'biotech',
    'immunization': 'vaccines',
    'chronic_disease': 'monitor_heart',
    'preventive_visit': 'event_available',
  };
  return icons[category] || 'help_outline';
}

/**
 * Get color for priority level
 * @param {string} priority - Priority level
 * @returns {string} Color code
 */
export function getPriorityColor(priority) {
  const colors = {
    'high': '#f44336',    // Red
    'medium': '#ff9800',  // Orange
    'low': '#4caf50',     // Green
  };
  return colors[priority] || '#9e9e9e';
}

/**
 * Get color for urgency level
 * @param {string} urgency - Urgency level
 * @returns {string} Color code
 */
export function getUrgencyColor(urgency) {
  const colors = {
    'severely_overdue': '#d32f2f',  // Dark red
    'overdue': '#f44336',            // Red
    'due_soon': '#ff9800',           // Orange
    'upcoming': '#4caf50',           // Green
  };
  return colors[urgency] || '#9e9e9e';
}

/**
 * Get label for urgency level
 * @param {string} urgency - Urgency level
 * @returns {string} Human-readable label
 */
export function getUrgencyLabel(urgency) {
  const labels = {
    'severely_overdue': 'Severely Overdue',
    'overdue': 'Overdue',
    'due_soon': 'Due Soon',
    'upcoming': 'Upcoming',
  };
  return labels[urgency] || 'Unknown';
}

/**
 * Get risk tier color
 * @param {string} tier - Risk tier
 * @returns {string} Color code
 */
export function getCareGapRiskTierColor(tier) {
  const colors = {
    'high_risk': '#d32f2f',
    'rising_risk': '#ff9800',
    'moderate_risk': '#2196F3',
    'low_risk': '#4caf50',
  };
  return colors[tier] || '#9e9e9e';
}

/**
 * Get risk tier label
 * @param {string} tier - Risk tier
 * @returns {string} Human-readable label
 */
export function getCareGapRiskTierLabel(tier) {
  const labels = {
    'high_risk': 'High Risk',
    'rising_risk': 'Rising Risk',
    'moderate_risk': 'Moderate Risk',
    'low_risk': 'Low Risk',
  };
  return labels[tier] || 'Unknown';
}

/**
 * Get outreach method icon
 * @param {string} method - Outreach method
 * @returns {string} Icon name
 */
export function getOutreachMethodIcon(method) {
  const icons = {
    'phone_call': 'phone',
    'patient_portal': 'computer',
    'mail': 'mail',
    'in_person': 'person',
    'sms': 'sms',
  };
  return icons[method] || 'contact_mail';
}

/**
 * Get outreach method label
 * @param {string} method - Outreach method
 * @returns {string} Human-readable label
 */
export function getOutreachMethodLabel(method) {
  const labels = {
    'phone_call': 'Phone Call',
    'patient_portal': 'Patient Portal',
    'mail': 'Mail',
    'in_person': 'In Person',
    'sms': 'SMS',
  };
  return labels[method] || 'Other';
}

/**
 * Format days overdue for display
 * @param {number} days - Days overdue
 * @returns {string} Formatted string
 */
export function formatDaysOverdue(days) {
  if (days === null || days === undefined) {
    return 'N/A';
  }
  if (days <= 0) {
    return 'Not yet due';
  }
  if (days === 1) {
    return '1 day overdue';
  }
  if (days < 30) {
    return `${days} days overdue`;
  }
  if (days < 365) {
    const months = Math.floor(days / 30);
    return `${months} month${months > 1 ? 's' : ''} overdue`;
  }
  const years = Math.floor(days / 365);
  return `${years} year${years > 1 ? 's' : ''} overdue`;
}

/**
 * Get gap type display name
 * @param {string} gapType - Gap type code
 * @returns {string} Display name
 */
export function getGapTypeDisplayName(gapType) {
  const names = {
    'BCS': 'Breast Cancer Screening',
    'COL': 'Colorectal Cancer Screening',
    'CCS': 'Cervical Cancer Screening',
    'LCS': 'Lung Cancer Screening',
    'OST': 'Osteoporosis Screening',
    'DSF': 'Depression Screening',
    'FLU': 'Influenza Vaccine',
    'PPSV23': 'Pneumococcal Vaccine (PPSV23)',
    'PCV': 'Pneumococcal Vaccine (PCV)',
    'HZV': 'Shingles Vaccine',
    'TDAP': 'Tdap Vaccine',
    'COVID': 'COVID-19 Vaccine',
    'HBD': 'HbA1c Testing (Diabetes)',
    'EED': 'Diabetic Eye Exam',
    'LDL': 'LDL Cholesterol Testing',
    'CBP': 'Blood Pressure Control',
    'CKD': 'Kidney Function Monitoring',
    'AWV': 'Annual Wellness Visit',
  };
  return names[gapType] || gapType;
}

/**
 * Get category display name
 * @param {string} category - Category code
 * @returns {string} Display name
 */
export function getCategoryDisplayName(category) {
  const names = {
    'screening': 'Preventive Screenings',
    'immunization': 'Immunizations',
    'chronic_disease': 'Chronic Disease Management',
    'preventive_visit': 'Preventive Visits',
  };
  return names[category] || category;
}
